import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, ShieldCheck } from 'lucide-react';
import { api } from '../../api/client';
import { useLanguage } from '../../context/LanguageContext';
import { showToast } from '../../components/common/Toast';
import { formatUzPhone, isCompleteUzPhone } from '../../utils/helpers';

export default function ContactPage() {
  const { t, currentLang } = useLanguage();
  const [contact, setContact] = useState(null);
  const [formData, setFormData] = useState({ name: '', phone: '+998 ()', email: '', message: '' });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get('/cms/contact')
      .then((res) => {
        if (res?.success) setContact(res.contact);
      });
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !isCompleteUzPhone(formData.phone) || !formData.message.trim()) {
      showToast(
        currentLang === 'uz'
          ? 'Ism, to‘liq telefon raqam (+998 (97) 325-07-70) va xabarni kiriting'
          : currentLang === 'ru'
          ? 'Заполните имя, полный номер телефона (+998 (97) 325-07-70) и текст сообщения'
          : 'Please enter your name, full phone number (+998 (97) 325-07-70), and message',
        'error'
      );
      return;
    }
    setSent(true);
    showToast(
      currentLang === 'uz'
        ? 'Xabaringiz qabul qilindi. Tez orada siz bilan bog‘lanamiz.'
        : currentLang === 'ru'
        ? 'Ваше сообщение принято. Мы свяжемся с вами в ближайшее время.'
        : 'Your message has been received. We will contact you soon.',
      'success'
    );
  };

  return (
    <div className="bg-[#05080e] min-h-screen text-slate-200">
      {/* Header */}
      <section className="bg-[#070c16] text-white py-14 sm:py-20 border-b border-[#141d2d] text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <Link
            to="/"
            className="inline-flex items-center text-xs font-semibold uppercase tracking-wider text-[#c59b27] hover:underline mb-4"
          >
            &larr; {currentLang === 'uz' ? 'Bosh sahifaga qaytish' : currentLang === 'ru' ? 'Назад на главную' : 'Back to Home'}
          </Link>
          <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#c59b27] block mb-2">
            {t('firm_name_badge', 'JIZZAX ADVOKATLARI')}
          </span>
          <h1 className="font-serif text-3xl sm:text-5xl font-bold text-slate-100 uppercase">
            {t('nav_contact', 'Biz Bilan Bog‘laning')}
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-4 font-light leading-relaxed">
            {t('contact_page_desc', 'Savollaringiz yoki shoshilinch yuridik yordam zarurati bo‘yicha to‘g‘ridan-to‘g‘ri murojaat qiling.')}
          </p>
        </div>
      </section>

      {/* Main Grid */}
      <section className="py-16 sm:py-24 bg-[#05080e]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="font-serif text-2xl font-bold text-slate-100 mb-6 uppercase">
                  {t('office_details_title', 'Markaziy Bosh Ofis Rekvizitlari')}
                </h2>
                <div className="space-y-4 text-xs text-slate-300">
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-[#c59b27] mr-3 shrink-0 mt-0.5" />
                    <span>
                      {currentLang === 'ru'
                        ? (contact?.addressRu || "г. Джизак, улица Ислама Каримова, напротив Турон Банка")
                        : currentLang === 'en'
                        ? (contact?.addressEn || "Jizzakh city, Islam Karimov street, opposite Turon Bank")
                        : (contact?.addressUz || "Jizzax shahar, Islom Karimov ko'chasi, Turon Bank ro'parasi")}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 text-[#c59b27] mr-3 shrink-0" />
                    <a href={`tel:${contact?.phone ? contact.phone.replace(/\D/g, '') : '+998973250770'}`} className="hover:text-[#c59b27] font-mono">
                      {contact?.phone ? formatUzPhone(contact.phone) : '+998 (97) 325-07-70'}
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Clock className="w-5 h-5 text-[#c59b27] mr-3 shrink-0" />
                    <span>
                      {currentLang === 'ru'
                        ? 'Понедельник - Суббота: 09:00 - 18:00 (Воскресенье: Выходной)'
                        : currentLang === 'en'
                        ? 'Monday - Saturday: 09:00 - 18:00 (Sunday: Day Off)'
                        : (contact?.workingHours || 'Dushanba - Shanba: 09:00 - 18:00 (Yakshanba: Dam olish)')}
                    </span>
                  </div>
                  <div className="flex items-center text-[#c59b27]">
                    <ShieldCheck className="w-5 h-5 text-[#c59b27] mr-3 shrink-0" />
                    <span className="font-semibold uppercase tracking-wider text-xs">
                      {t('free_consultation_badge', 'Bizda konsultatsiya bepul')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Emergency Banner */}
              <div className="p-6 rounded-xl bg-[#090e18] border border-[#c59b27]/40 shadow-xl">
                <span className="text-[10px] uppercase font-bold tracking-wider text-[#c59b27] block mb-1">
                  {t('emergency_call_title', 'SHOSHILINCH ADVOKAT CHAQIRUVI (24/7)')}
                </span>
                <p className="text-slate-300 text-xs font-light mb-3">
                  {t('emergency_call_desc', 'Qamoqqa olish, tergov harakatlari yoki kechiktirib bo‘lmaydigan tintuvlarda shoshilinch advokat xizmati:')}
                </p>
                <a
                  href={`tel:${contact?.emergencyPhone ? contact.emergencyPhone.replace(/\D/g, '') : '+998973250770'}`}
                  className="font-mono text-base font-bold text-[#c59b27] hover:underline"
                >
                  {contact?.emergencyPhone ? formatUzPhone(contact.emergencyPhone) : '+998 (97) 325-07-70'}
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="bg-[#090e18] border border-[#1b2b46] rounded-xl p-8 shadow-2xl">
              <h3 className="font-serif text-xl font-bold text-slate-100 mb-6 uppercase">
                {t('quick_msg_title', 'Tezkor Xabar Qoldirish')}
              </h3>

              {sent ? (
                <div className="text-center py-10 space-y-3">
                  <CheckCircle2 className="w-12 h-12 text-[#c59b27] mx-auto" />
                  <h4 className="font-serif text-lg font-bold text-slate-100 uppercase">
                    {t('msg_sent_title', 'Xabaringiz Qabul Qilindi')}
                  </h4>
                  <p className="text-slate-400 text-xs">
                    {t('msg_sent_desc', 'Advokatlarimiz tez orada siz bilan bog‘lanadi.')}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-xs uppercase text-slate-400 font-semibold mb-1">
                      {t('client_name_label', 'Ism va Familiya')} *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder={currentLang === 'uz' ? 'Ismingiz' : currentLang === 'ru' ? 'Ваше имя' : 'Your name'}
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-[#060a12] border border-[#1b2b46] rounded text-xs text-slate-100 focus:outline-none focus:border-[#c59b27]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase text-slate-400 font-semibold mb-1">
                      {t('phone_or_email_label', 'Telefon Raqami')} *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="+998 (97) 325-07-70"
                      value={formData.phone || '+998 ()'}
                      onFocus={() => {
                        if (!formData.phone) setFormData((p) => ({ ...p, phone: '+998 ()' }));
                      }}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          phone: formatUzPhone(e.target.value, prev.phone),
                        }))
                      }
                      className="w-full px-3.5 py-2.5 bg-[#060a12] border border-[#1b2b46] rounded text-xs text-slate-100 focus:outline-none focus:border-[#c59b27] font-mono"
                    />
                  </div>


                  <div>
                    <label className="block text-xs uppercase text-slate-400 font-semibold mb-1">
                      {t('msg_content_label', 'Xabar Mazmuni')} *
                    </label>
                    <textarea
                      required
                      rows={4}
                      placeholder={t('msg_content_placeholder', 'Savolingiz yoki yuridik masala haqida qisqacha...')}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-3.5 py-2.5 bg-[#060a12] border border-[#1b2b46] rounded text-xs text-slate-100 focus:outline-none focus:border-[#c59b27] resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3 px-4 rounded bg-[#b38038] hover:bg-[#c59b27] text-white text-xs font-bold uppercase tracking-wider shadow-lg transition-all flex items-center justify-center cursor-pointer"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {t('send_msg_btn', 'XABARNI YUBORISH')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
